import {
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Frame } from '../../components/layouts/Frame';
import StripePaymentMethods from '../../assets/images/stripe-payment-methods.png';
import { useEffect, useState } from 'react';
import { BuyCreditData, CountryData, CreditData } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useAPI } from '../../hooks/useAPI';
import { useParams } from 'react-router-dom';
import StripePayment from './StripePayment';

export const BuyCredit = () => {
  const { isAuthenticated, user } = useAuth();
  const params = useParams();
  const creditId = params.credit_id;
  const [countries, setCountries] = useState<CountryData[]>([]);
  const [credit, setCredit] = useState<CreditData | null>(null);
  const [buyData, setBuyData] = useState<BuyCreditData>({});
  const [error, setError] = useState<BuyCreditData>({});
  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const [amount, setAmount] = useState(0);
  const { fetchData } = useAPI();

  const onBeforeSubmit = async () => {
    const { errors, data } = await fetchData(
      `/credits/buy-validate`,
      buyData,
      'POST',
    );
    if (errors) {
      setError(errors);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData(`/credits/${creditId}`).then(({ errors, data }) => {
        setCredit(data);
      });
      fetchData(`/countries`).then(({ errors, data }) => {
        setCountries(data);
      });
    }
  }, [isAuthenticated, creditId]);

  useEffect(() => {
    if (user && countries.length > 0) {
      setBuyData(prev => ({
        ...prev,
        billing_name:
          user.first_name && user.last_name
            ? `${user.first_name} ${user.last_name}`
            : undefined,
        company_name: user.company_name,
        billing_email: user.email,
        billing_country:
          countries.find(item => item.code === 'GB')?.id ??
          prev.billing_country,
      }));
    }
  }, [user, countries]);

  useEffect(() => {
    const newParams = new URLSearchParams();

    for (const key in buyData) {
      //@ts-ignore
      newParams.append(key, String(buyData[key]));
    }

    const paramString = newParams.toString();
    setRedirectUrl(
      `${process.env.REACT_APP_URL}/buy-credit/${creditId}/checkout-complete?${paramString}`,
    );

    if (countries.length > 0 && buyData.billing_country > 0) {
      const selCountry = countries.find(
        (itm: CountryData) => itm.id === buyData.billing_country,
      );
      const creditPrice = credit?.price ?? 0;

      if (selCountry) {
        setAmount(creditPrice * (selCountry.code === 'GB' ? 1.2 : 1));
      }
    }
  }, [buyData]);

  return (
    <Frame title="Buy Credits">
      <Stack spacing={4}>
        <Stack>
          <Typography>
            1 x Credit = 1 x &nbsp;
            <span style={{ fontWeight: '600' }}>
              Low Detail Image Query
            </span>{' '}
            &nbsp; (768 x 576 px max)
          </Typography>
          <Typography>
            2 x Credit = 1 x &nbsp;
            <span style={{ fontWeight: '600' }}>
              Medium Detail Image Query
            </span>{' '}
            &nbsp; (1024 x 768 px max)
          </Typography>
          <Typography>
            3 x Credit = 1 x &nbsp;
            <span style={{ fontWeight: '600' }}>
              High Detail Image Query
            </span>{' '}
            &nbsp; (2048 x 1536 px max)
          </Typography>
        </Stack>
        <Stack
          sx={{
            flexDirection: 'row',
            gap: '64px',
          }}
          spacing={8}>
          <Stack width="100%">
            <Typography variant="h4" fontWeight="600">
              You are buying {credit?.count?.toLocaleString() ?? '{_}'} credits
            </Typography>
            <Stack mt={2} spacing={3}>
              <Stack width="100%">
                <Typography>
                  Billing Name <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  variant="outlined"
                  name="billing_name"
                  value={buyData.billing_name}
                  error={error.billing_name ? true : false}
                  helperText={error?.billing_name?.[0] ?? ''}
                  onChange={e =>
                    setBuyData({ ...buyData, billing_name: e.target.value })
                  }
                />
              </Stack>
              <Stack width="100%">
                <Typography>Company Name</Typography>
                <TextField
                  variant="outlined"
                  name="company_name"
                  value={buyData.company_name}
                  error={error.company_name ? true : false}
                  helperText={error?.company_name?.[0] ?? ''}
                  onChange={e =>
                    setBuyData({ ...buyData, company_name: e.target.value })
                  }
                />
              </Stack>
              <Stack width="100%">
                <Typography>
                  Email <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  variant="outlined"
                  name="billing_email"
                  type="email"
                  value={buyData.billing_email}
                  error={error.billing_email ? true : false}
                  helperText={error?.billing_email?.[0] ?? ''}
                  onChange={e =>
                    setBuyData({ ...buyData, billing_email: e.target.value })
                  }
                />
              </Stack>
              <Typography fontWeight="600" pt={2}>
                Billing Address
              </Typography>
              <Stack width="100%">
                <Typography>
                  Street Address <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  variant="outlined"
                  name="billing_address"
                  value={buyData.billing_address}
                  error={error.billing_address ? true : false}
                  helperText={error?.billing_address?.[0] ?? ''}
                  onChange={e =>
                    setBuyData({ ...buyData, billing_address: e.target.value })
                  }
                />
              </Stack>
              <Stack width="100%">
                <Typography>
                  City <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  variant="outlined"
                  name="billing_city"
                  value={buyData.billing_city}
                  error={error.billing_city ? true : false}
                  helperText={error?.billing_city?.[0] ?? ''}
                  onChange={e =>
                    setBuyData({ ...buyData, billing_city: e.target.value })
                  }
                />
              </Stack>
              <Stack width="100%">
                <Typography>
                  County <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  variant="outlined"
                  name="billing_county"
                  value={buyData.billing_county}
                  error={error.billing_county ? true : false}
                  helperText={error?.billing_county?.[0] ?? ''}
                  onChange={e =>
                    setBuyData({ ...buyData, billing_county: e.target.value })
                  }
                />
              </Stack>
              <Stack width="100%">
                <Typography>
                  Postcode <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  variant="outlined"
                  name="billing_postcode"
                  value={buyData.billing_postcode}
                  error={error.billing_postcode ? true : false}
                  helperText={error?.billing_postcode?.[0] ?? ''}
                  onChange={e =>
                    setBuyData({ ...buyData, billing_postcode: e.target.value })
                  }
                />
              </Stack>
              <Stack width="100%">
                <FormControl error={error.billing_country ? true : false}>
                  <Typography>
                    Country <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Select
                    sx={{ width: '100%' }}
                    name="billing_country"
                    value={buyData.billing_country ?? ''}
                    onChange={(e: any) =>
                      setBuyData({
                        ...buyData,
                        billing_country: e.target.value,
                      })
                    }>
                    {countries
                      .filter((item: CountryData) => item.code === 'GB')
                      .map((item: CountryData, index: number) => (
                        <MenuItem value={item.id} key={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    {countries
                      .filter((item: CountryData) => item.code !== 'GB')
                      .map((item: CountryData, index: number) => (
                        <MenuItem value={item.id} key={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                  </Select>
                  {error.billing_country ? (
                    <FormHelperText
                      color="#ff1744"
                      style={{ marginBottom: '-20px' }}>
                      {error.billing_country?.[0] ?? ''}
                    </FormHelperText>
                  ) : null}
                </FormControl>
              </Stack>
            </Stack>
          </Stack>
          <Stack>
            <Stack width="100%" mt="0px">
              <img src={StripePaymentMethods} width="100%" />
            </Stack>
            <Stack></Stack>
            <Stack flexDirection="row" padding={4}>
              <StripePayment
                amount={amount}
                redirectUrl={redirectUrl}
                onBeforeSubmit={onBeforeSubmit}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Frame>
  );
};
